import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import React, { useEffect, useState } from 'react';
import { useCallback } from 'react';
import { FaCheck } from 'react-icons/fa';

import cn from '@/lib/clsxm';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import Container from '@/components/@base/container';
import { CONTAINER_TAG_TYPE } from '@/components/@base/container/type';
import List, { ListItem } from '@/components/@base/list';
import { showToast } from '@/components/@base/toast/show-toast';
import Props from '@/components/app/cart/components/list/item/features/type';
import { usePostAddToCart, useRemoveFromCart } from '@/components/app/cart/services/hooks';
import InstallationRulesButton from '@/components/app/product/components/side-bar/installation-rules-button';
import OptionsItem from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item';
import { ADDONS_ENUM } from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/type';

import { useAddonsStore } from '@/store/addons.store';

import { ICON_COLOR } from '@/constant/icon-size-color';
import { pageLevelLocalization } from '@/constant/localization';
import { GET_CART } from '@/constant/query-key';
import AddonModel from '@/models/addon.model';
const { error: errorLocalization, product: productLocalization } = pageLevelLocalization;
export default function Features({
  data,
  visible,
  dataProduct,
  addonList,
  selectedAddonList,
}: Props) {
  const isSmallScreen = useClientSmallScreen();

  const [rendered, setRendered] = useState(visible);
  const [itemLoading, setItemLoading] = useState<string | null>(null);
  useEffect(() => {
    if (visible) {
      setRendered(true);
    }
  }, [visible]);

  const handleAnimationEnd = () => {
    if (!visible) {
      setRendered(false);
    }
  };
  const queryClient = useQueryClient();

  const { mutate } = usePostAddToCart();
  const { mutate: Remove } = useRemoveFromCart();
  const { selectedAddons, setSelectedAddons, setNewSelectedAddons, newSelectedAddons } =
    useAddonsStore();

  useEffect(() => {
    if (selectedAddonList?.length > 0) {
      const updatedAddOnsFromList: {
        [key: string]: { selected: boolean; price: number; id?: string };
      } = selectedAddons;
      selectedAddonList.forEach((item) => {
        if ((item?.addon as AddonModel).type === 'vendor-addon-service') {
          updatedAddOnsFromList[item?.params.extra_addon] = {
            selected: true,
            price: (item?.addon as AddonModel).price || 0,
            id: item.product_id,
          };
        } else if ((item?.addon as AddonModel).type) {
          updatedAddOnsFromList[(item?.addon as AddonModel).type] = {
            selected: true,
            price: (item?.addon as AddonModel).price || 0,
            id: item.product_id,
          };
        }
      });
      setSelectedAddons(updatedAddOnsFromList);
    }
  }, []);
  const debouncedMutate = useCallback(
    debounce(
      (
        id: string,
        slug: string[],
        updatedAddOns: typeof selectedAddons,
        updatedNewAddOns: typeof newSelectedAddons,
        key: string,
      ) => {
        mutate(
          { id: dataProduct.product_id, slug: key },
          {
            onSuccess: (res) => {
              if (res.appliedCodeSteppedDiscount) {
                showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
              }
              queryClient.setQueryData([GET_CART], res);
              setItemLoading(null);
              setSelectedAddons(updatedAddOns);
              setNewSelectedAddons(updatedNewAddOns);
            },
            onError: () => {
              setItemLoading(null);
              showToast({
                message: errorLocalization.message429 + errorLocalization.message429_try_again,
                type: 'error',
              });
            },
          },
        );
      },
      300,
    ),
    [mutate, queryClient, setSelectedAddons, setNewSelectedAddons],
  );
  const debouncedRemoveMutate = useCallback(
    debounce(
      (
        id: string,
        slug: string[],
        updatedAddOns: typeof selectedAddons,
        updatedNewAddOns: typeof newSelectedAddons,
        key: string,
      ) => {
        Remove(
          { id: dataProduct.product_id, slug: key },
          {
            onSuccess: (res) => {
              if (res.appliedCodeSteppedDiscount) {
                showToast({ message: res.appliedCodeSteppedDiscount, type: 'default' });
              }
              queryClient.setQueryData([GET_CART], res);
              setItemLoading(null);
              setSelectedAddons(updatedAddOns);
              setNewSelectedAddons(updatedNewAddOns);
            },
            onError: () => {
              setItemLoading(null);
              showToast({
                message: errorLocalization.message429 + errorLocalization.message429_try_again,
                type: 'error',
              });
            },
          },
        );
      },
      300,
    ),
    [Remove, queryClient, setSelectedAddons, setNewSelectedAddons],
  );

  const handleAddOnsChange = (key: string, checked: boolean, price: number, id: string) => {
    setItemLoading(key);
    let updatedAddOns = { ...selectedAddons };
    let updatedNewAddOns = { ...newSelectedAddons };
    if (checked) {
      updatedAddOns[key] = { selected: checked, price: price, id: id };
      updatedNewAddOns[key] = { selected: checked, price: price, id: id };
    } else {
      delete updatedAddOns[key];
      delete updatedNewAddOns[key];
    }

    const getSelectedAddonSlugs = (addonsObject: {
      [key: string]: { selected: boolean; price: number; id?: string };
    }) => {
      return Object.keys(addonsObject).filter(
        (item) =>
          addonsObject[item].selected && addonsObject[item].id === dataProduct.product_id,
      );
    };

    const newAddonsListToSend = getSelectedAddonSlugs(updatedAddOns);
    if (dataProduct?.product_id) {
      if (checked) {
        debouncedMutate(
          dataProduct.product_id,
          newAddonsListToSend,
          updatedAddOns,
          updatedNewAddOns,
          key,
        );
      } else {
        debouncedRemoveMutate(
          dataProduct.product_id,
          newAddonsListToSend,
          updatedAddOns,
          updatedNewAddOns,
          key,
        );
      }
    }
  };
  return (
    <Container
      className={cn(
        'flex',
        'z-20! -mr-4! w-full! min-w-[225px]! transform overflow-hidden overflow-visible transition-all duration-300 md:-mr-0!',
        visible
          ? 'max-h-full translate-y-0 opacity-100'
          : 'max-h-0 translate-y-[-20px] opacity-0',
        !rendered && 'hidden',
      )}
      onAnimationEnd={handleAnimationEnd}
    >
      <List className='mx-0! mt-3 mb-3 flex w-full flex-col gap-2 rounded-lg bg-[#F7F8F9] p-3 md:mt-2 md:flex-row md:p-2'>
        {addonList && addonList?.length > 0 && (
          <>
            <Container
              className={clsx(
                data && data?.length > 0 && 'md:w-[60%]!',
                'flex w-full flex-col md:w-full',
              )}
            >
              {addonList
                ?.slice(0, 4)
                ?.map((item) => (
                  <OptionsItem
                    labelClassName='text-[#76767C]! text-[13px]! max-w-[145px] truncate break-all'
                    key={item.type}
                    typeItem={item.type}
                    label={item.title}
                    price={item.price}
                    hasCheckbox
                    onChange={(checked) =>
                      handleAddOnsChange(
                        item.type,
                        checked,
                        item.price,
                        dataProduct.product_id,
                      )
                    }
                    checked={
                      newSelectedAddons[item.type]?.id === dataProduct?.product_id &&
                      newSelectedAddons[item.type]?.selected
                    }
                    additionalItems={
                      item.type === ADDONS_ENUM.INSTALLATION &&
                      !isSmallScreen && <InstallationRulesButton />
                    }
                    subTitle={item.subTitle}
                    showCurrency={false}
                    finalLastPrice={item.price}
                    discount={item.discount || 0}
                    lastPrice={item.lastPrice || 0}
                    className='h-[48px]! py-0!'
                    isLoading={itemLoading === item.type}
                    finalLastPriceClassName='text-[16px]!'
                    lastPriceClassName='text-xs!'
                    priceClassName='mr-1'
                  />
                ))}
            </Container>
            {data && data?.length > 0 && (
              <div className='mt-[2.5%] h-[88%] w-[1px] bg-[#E5E8EB]'></div>
            )}
          </>
        )}

        {data && data?.length > 0 && (
          <Container
            className={clsx(
              addonList && addonList?.length === 0 && 'w-full!',
              'flex hidden w-full flex-col gap-2 md:inline md:w-[40%]!',
            )}
          >
            {addonList && addonList?.length > 0 && (
              <Container
                type={CONTAINER_TAG_TYPE.HEADER}
                className='mt-2 mb-1.5! text-[13px] leading-[24px] text-[#424B57]'
              >
                {productLocalization.highlights}
              </Container>
            )}
            {data?.map((item) => (
              <ListItem
                className='flex gap-2 text-[13px] leading-[24px] text-[#76767C]'
                key={item}
              >
                <FaCheck
                  color={ICON_COLOR.purple}
                  className='mt-1.5 size-[10px] min-w-[12px]'
                />
                {item}
              </ListItem>
            ))}
          </Container>
        )}
      </List>
    </Container>
  );
}
